import { coerceDateTime } from '../my-journeys/myJourneysModel';
import { classifyLogisticsError, snowflakeId } from '../journey-accommodation/accommodationModel';
import {
  memberDisplayName,
  memberFallbackLabel,
} from '../member-display/memberDisplayModel';

export const CHECKLIST_STATUS = Object.freeze({
  OPEN: 'OPEN',
  COMPLETED: 'COMPLETED',
});

/** Create-time assignee roles — no VIEWER. */
export const CHECKLIST_ASSIGNEE_ROLES = Object.freeze(['OWNER', 'COORDINATOR', 'MEMBER']);

const ASSIGNEE_ROLE_SET = new Set(CHECKLIST_ASSIGNEE_ROLES);

export function normalizeChecklistItem(value) {
  const status = String(value?.status || CHECKLIST_STATUS.OPEN).toUpperCase();
  const completed = status === CHECKLIST_STATUS.COMPLETED;
  return {
    itemId: snowflakeId(value?.itemId),
    checklistId: snowflakeId(value?.checklistId),
    journeyId: snowflakeId(value?.journeyId),
    title: value?.title || '',
    description: value?.description || '',
    assignedMemberId: snowflakeId(value?.assignedMemberId),
    dueAt: coerceDateTime(value?.dueAt),
    status: completed ? CHECKLIST_STATUS.COMPLETED : CHECKLIST_STATUS.OPEN,
    completed,
    completedAt: coerceDateTime(value?.completedAt),
    completedByMemberId: snowflakeId(value?.completedByMemberId),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    removed: Boolean(value?.removed),
  };
}

export function normalizeChecklistBoard(value) {
  const items = Array.isArray(value?.items) ? value.items.map(normalizeChecklistItem) : [];
  return {
    journeyId: snowflakeId(value?.journeyId),
    checklistId: snowflakeId(value?.checklistId),
    items,
  };
}

/** Presentation-only progress derived from loaded items — not persisted truth. */
export function deriveChecklistProgress(items) {
  const list = Array.isArray(items) ? items : [];
  const total = list.length;
  const completed = list.filter((item) => item.completed).length;
  return {
    total,
    completed,
    remaining: Math.max(0, total - completed),
    label: total === 0 ? '0 completed' : `${completed} / ${total} completed`,
  };
}

export function filterChecklistItems(items, filter) {
  const list = Array.isArray(items) ? items : [];
  if (filter === 'open') return list.filter((item) => !item.completed);
  if (filter === 'completed') return list.filter((item) => item.completed);
  return list;
}

/**
 * ACTIVE OWNER／COORDINATOR／MEMBER only.
 * LEFT／REMOVED／pending invitation are never selectable.
 */
export function eligibleChecklistAssignees(members) {
  const list = Array.isArray(members) ? members : [];
  return list.filter((member) => (
    member?.membershipStatus === 'ACTIVE'
    && ASSIGNEE_ROLE_SET.has(member?.role)
  ));
}

/**
 * Unassigned: omit assignedMemberId（後端 nullable；不送 0／空字串）.
 * Assigned: send assignedMemberId as string (snowflake-safe).
 */
/**
 * Dedicated assignee command always sends the field.
 * null unassigns; positive id assigns. Same value is a backend no-op.
 */
export function buildChecklistReassignPayload(assignedMemberId) {
  if (assignedMemberId == null || String(assignedMemberId).trim() === '') {
    return { assignedMemberId: null };
  }
  return { assignedMemberId: String(assignedMemberId).trim() };
}

export function checklistReassignSelectValue(assignedMemberId, eligibleMembers) {
  if (assignedMemberId == null || assignedMemberId === '') return '';
  const id = String(assignedMemberId);
  const eligible = (Array.isArray(eligibleMembers) ? eligibleMembers : [])
    .some((member) => String(member?.memberId) === id);
  return eligible ? id : `historical:${id}`;
}

export function buildChecklistCreatePayload(draft) {
  const body = {
    title: String(draft?.title || '').trim(),
    description: String(draft?.description || '').trim() || null,
  };
  const raw = draft?.assignedMemberId;
  if (raw != null && String(raw).trim() !== '') {
    body.assignedMemberId = String(raw).trim();
  }
  return body;
}

export function validateChecklistCreateDraft(draft) {
  const title = String(draft?.title || '').trim();
  if (!title) return 'Title is required.';
  if (title.length > 200) return 'Title must be at most 200 characters.';
  return '';
}

/**
 * Render assignment without confusing historical assignee as Unassigned.
 * @returns {{ kind: 'unassigned'|'active'|'historical'|'historical-unknown', label: string, memberId: string|null }}
 */
export function resolveChecklistAssigneePresentation({
  assignedMemberId,
  members,
  summariesById,
} = {}) {
  if (assignedMemberId == null || assignedMemberId === '') {
    return { kind: 'unassigned', label: '未指派', memberId: null };
  }
  const id = String(assignedMemberId);
  const member = (Array.isArray(members) ? members : [])
    .find((row) => String(row.memberId) === id);
  const display = memberDisplayName(id, summariesById);
  if (member?.membershipStatus === 'ACTIVE' && ASSIGNEE_ROLE_SET.has(member.role)) {
    return { kind: 'active', label: display, memberId: id };
  }
  if (member) {
    return {
      kind: 'historical',
      label: display,
      memberId: id,
      membershipStatus: member.membershipStatus,
    };
  }
  return {
    kind: 'historical-unknown',
    label: display || memberFallbackLabel(id),
    memberId: id,
  };
}

export function checklistAssigneeErrorMessage(cause) {
  const detail = cause?.raw?.message || cause?.message || '';
  if (/ACTIVE journey member|assignedMemberId/i.test(String(detail))) {
    return '所選旅伴已無法指派，請重新選擇後再試。';
  }
  return '';
}

export { classifyLogisticsError, snowflakeId };
